import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  animate,
  style,
  transition,
  trigger
} from '@angular/animations';
import { CharCodes } from '../domain/char-codes';
import { ActivatedRoute } from '@angular/router';
import { VgApiService } from '@videogular/ngx-videogular/core';
import type { HlsConfig } from 'hls.js';
import { interval, Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Game } from '../domain/game';
import { FLOORBALL_PITCH, ICE_RINK, Pitch } from '../domain/pitch';
import { Shift } from '../domain/shift';
import { VideoService } from '../services/video.service';
import { GameTimeService } from '../services/game-time.service';
import { ShiftsService } from '../services/shifts.service';
import {
  selectGameTime,
  selectPeriod
} from '../state/reducers/game-event.reducer';
import { GlobalState } from '../state/reducers';

export type ShowcaseStageId =
  | 'tracking'
  | 'teams'
  | 'players'
  | 'coordinate_mapping'
  | 'time_on_ice';

export interface ShowcaseStage {
  id: ShowcaseStageId;
  title: string;
  subtitle: string;
  durationInSeconds: number;
  enabled: boolean;
}

@Component({
  selector: 'app-cv-showcase-video-player-window',
  templateUrl: './cv-showcase-video-player-window.component.html',
  styleUrls: ['./cv-showcase-video-player-window.component.css'],
  animations: [
    trigger('stageFade', [
      transition(
        ':enter',
        [
          style({ opacity: 0 }),
          animate('{{ fadeInMs }}ms ease-out', style({ opacity: 1 }))
        ],
        { params: { fadeInMs: 2000 } }
      ),
      transition(
        ':leave',
        [
          style({
            opacity: 1,
            position: 'absolute',
            bottom: 0,
            left: 0
          }),
          animate('{{ fadeOutMs }}ms ease-in', style({ opacity: 0 }))
        ],
        { params: { fadeOutMs: 1500 } }
      )
    ])
  ]
})
export class CvShowcaseVideoPlayerWindowComponent
  implements OnInit, OnDestroy
{
  private componentDestroyed$: Subject<void> = new Subject();

  game: Game;
  videoUrl: string;
  selectedVideoMediaId: string;
  mediaPlayerApi: VgApiService;
  hlsConfig: Partial<HlsConfig> = {};

  videoWidth: number;
  videoHeight: number;
  frameRate: number;
  trackOffset = 0;

  currentTime = 0;
  currentFrame = 0;
  videoPaused = true;

  private startVideoTime: number;
  private showcaseShifts: Shift[] = [];
  private currentPeriod: string | null = null;
  private playerWired = false;
  private lastGameTimeDispatchAt = -Infinity;

  showcaseStage = -1;
  labelStage: number | null = null;
  configMenuVisible = true;
  showcaseStarted = false;
  showcaseIntro = false;
  showcaseComplete = false;
  introTitle = 'Computer Vision';
  pitch: Pitch = ICE_RINK;
  readonly pitchOptions: { label: string; pitch: Pitch }[] = [
    { label: 'Ice hockey rink', pitch: ICE_RINK },
    { label: 'Floorball pitch', pitch: FLOORBALL_PITCH }
  ];
  videoTimeInput = '00:00:00';
  private videoTimeInputFocused = false;
  readonly INTRO_DURATION_SECONDS = 5;
  readonly AUDIO_FADE_SECONDS = 1;
  // Stage label transition timings — configurable at runtime via the config
  // menu. The fade-in/fade-out values also drive the `stageFade` animation
  // (see labelFadeInMs / labelFadeOutMs), so they stay in sync automatically.
  labelFadeInSeconds = 2;
  labelHoldSeconds = 2;
  labelFadeOutSeconds = 1.5;
  transitionsExpanded = false;
  stages: ShowcaseStage[] = [
    {
      id: 'tracking',
      title: 'Tracking',
      subtitle: 'Following Players',
      durationInSeconds: 5,
      enabled: true
    },
    {
      id: 'teams',
      title: 'Teams',
      subtitle: 'Identifying Teams',
      durationInSeconds: 5,
      enabled: true
    },
    {
      id: 'players',
      title: 'Players',
      subtitle: 'Identifying Jersey No\'s',
      durationInSeconds: 5,
      enabled: true
    },
    {
      id: 'coordinate_mapping',
      title: 'Coordinate Mapping',
      subtitle: 'Video to Ice Rink',
      durationInSeconds: 5,
      enabled: true
    },
    {
      id: 'time_on_ice',
      title: 'Time on Ice',
      subtitle: 'Inferring Player Shifts',
      durationInSeconds: 5,
      enabled: true
    }
  ];

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const tag = (event.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
      return;
    }
    if (event.keyCode === CharCodes.space_key_code) {
      event.preventDefault();
      if (!this.mediaPlayerApi) {
        return;
      }
      if (this.videoPaused) {
        this.safePlay();
      } else {
        this.mediaPlayerApi.pause();
      }
    } else if (event.keyCode === CharCodes.escape_key_code) {
      if (document.fullscreenElement) {
        return;
      }
      if (!this.showcaseStarted) {
        return;
      }
      event.preventDefault();
      this.stopShowcase();
    }
  }

  stopShowcase() {
    this.showcaseStarted = false;
    this.showcaseIntro = false;
    this.showcaseComplete = false;
    this.configMenuVisible = true;
    this.showcaseStage = -1;
    this.labelStage = null;
    this.startVideoTime = undefined;
    if (this.mediaPlayerApi) {
      this.mediaPlayerApi.volume = 1;
      if (!this.videoPaused) {
        this.mediaPlayerApi.pause();
      }
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }

  startShowcase() {
    if (this.videoTimeInputFocused) {
      this.videoTimeInputFocused = false;
      this.applyVideoTime();
    }
    this.configMenuVisible = false;
    this.showcaseStarted = true;
    this.showcaseIntro = true;
    this.showcaseComplete = false;
    this.showcaseStage = -1;
    this.labelStage = null;
    // Reset the throttle so the first tick of the new run dispatches game
    // time immediately, even if the user is starting within 1s of where the
    // previous run last dispatched.
    this.lastGameTimeDispatchAt = -Infinity;
    const apiTime = this.mediaPlayerApi?.currentTime;
    if (apiTime != null && apiTime > 0) {
      this.startVideoTime = apiTime;
    } else if (this.startVideoTime == null) {
      this.startVideoTime = 0;
    }
    if (this.mediaPlayerApi) {
      this.mediaPlayerApi.volume = 0;
      this.safePlay();
    }
    const host = this.elementRef.nativeElement;
    if (host && host.requestFullscreen && !document.fullscreenElement) {
      host.requestFullscreen().catch(() => {});
    }
  }

  get activeStages(): ShowcaseStage[] {
    return this.stages.filter((s) => s.enabled);
  }

  get totalShowcaseDuration(): string {
    const totalSeconds =
      this.INTRO_DURATION_SECONDS + this.totalStagesDuration;
    const rounded = Math.round(totalSeconds);
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  isStageActive(id: ShowcaseStageId): boolean {
    const index = this.activeStages.findIndex((s) => s.id === id);
    return index >= 0 && this.showcaseStage >= index;
  }

  activeStageNumber(stage: ShowcaseStage): number {
    return this.activeStages.findIndex((s) => s.id === stage.id) + 1;
  }

  get currentShowcaseStage() {
    return this.activeStages[this.showcaseStage] ?? this.activeStages[0];
  }

  get labelStages() {
    return this.labelStage != null
      ? [this.activeStages[this.labelStage]]
      : [];
  }

  // Fade durations in ms for the `stageFade` animation params, kept in sync
  // with the configurable label transition timings.
  get labelFadeInMs(): number {
    return this.labelFadeInSeconds * 1000;
  }

  get labelFadeOutMs(): number {
    return this.labelFadeOutSeconds * 1000;
  }

  trackByStageTitle(_index: number, stage: { title: string }) {
    return stage.title;
  }

  onVideoTimeFocus() {
    this.videoTimeInputFocused = true;
  }

  onVideoTimeBlur() {
    this.videoTimeInputFocused = false;
    this.applyVideoTime();
  }

  applyVideoTime() {
    const seconds = this.parseTimeString(this.videoTimeInput);
    if (seconds == null) {
      this.videoTimeInput = this.formatTime(
        this.mediaPlayerApi?.currentTime ?? this.startVideoTime ?? 0
      );
      return;
    }
    if (this.mediaPlayerApi) {
      const duration = this.mediaPlayerApi.duration;
      if (duration && seconds > duration) {
        this.videoTimeInput = this.formatTime(this.mediaPlayerApi.currentTime);
        return;
      }
      this.videoTimeInput = this.formatTime(seconds);
      this.seekTo(seconds);
    } else {
      this.videoTimeInput = this.formatTime(seconds);
      this.startVideoTime = seconds;
    }
  }

  private formatTime(totalSeconds: number): string {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  private parseTimeString(value: string): number | null {
    const parts = value.trim().split(':');
    if (parts.length === 0 || parts.length > 3) {
      return null;
    }
    const numbers = parts.map((p) => parseInt(p, 10));
    if (numbers.some((n) => isNaN(n) || n < 0)) {
      return null;
    }
    let h = 0;
    let m = 0;
    let s = 0;
    if (numbers.length === 3) {
      [h, m, s] = numbers;
    } else if (numbers.length === 2) {
      [m, s] = numbers;
    } else {
      [s] = numbers;
    }
    return h * 3600 + m * 60 + s;
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }

  get showcaseRinkSwapped(): boolean {
    // Must match the set in tracking.service.ts#transformCoordinates —
    // overtime periods rotate the rink relative to the home team's start.
    const periodSwap =
      this.currentPeriod != null &&
      ['2', '4', '6'].includes(this.currentPeriod);
    const startSwap = this.game?.homeTeamStartPosition === 'right';
    return periodSwap !== startSwap;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private videoService: VideoService,
    private store: Store<GlobalState>,
    private gameTimeService: GameTimeService,
    private shiftsService: ShiftsService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit() {
    this.configHls();
    this.game = this.activatedRoute.snapshot.data['game'];
    this.videoService.prepareMediaData(this.game.videos ?? []);

    if ((this.game.videos ?? []).length > 0) {
      this.videoUrl = this.game.videos[0].urlSigned || this.game.videos[0].url;
      this.selectedVideoMediaId = this.game.videos[0].mediaId;
      this.frameRate =
        this.game.videos[0].frameRate ||
        this.getLeagueFrameRateDefault(this.game.league);
      this.trackOffset = this.game.videos[0].trackOffset ?? 0;
    }

    const videoTimeStr = this.activatedRoute.snapshot.queryParams['videoTime'];
    if (videoTimeStr) {
      const seconds = this.parseTimeString(videoTimeStr);
      if (seconds != null) {
        this.startVideoTime = seconds;
        this.currentFrame = Math.floor(seconds * this.frameRate);
      }
    }

    this.gameTimeService.init(this.game._id);
    this.shiftsService
      .getShifts(this.game)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((shifts) => {
        this.showcaseShifts = shifts ?? [];
      });

    this.store
      .select(selectPeriod)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((period) => (this.currentPeriod = period));

    this.store
      .select(selectGameTime)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((gameTime) => {
        if (gameTime != null && this.showcaseShifts.length > 0) {
          this.shiftsService.updateActiveShifts(
            gameTime,
            this.showcaseShifts
          );
        }
      });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
  }

  private getLeagueFrameRateDefault(league: string): number {
    if (league === '106') {
      return 50;
    }
    return 25;
  }

  get isHLS(): boolean {
    return !!this.videoUrl && this.videoUrl.indexOf('.m3u8') > -1;
  }

  get isMP4(): boolean {
    return !this.isHLS;
  }

  get activeMediaId(): string {
    return this.selectedVideoMediaId;
  }

  get mediaIds(): string[] {
    return this.game.videos && this.game.videos.length > 0
      ? this.game.videos.map((v: any) => v.mediaId)
      : ['video-vod-0', 'video-hls-0'];
  }

  onPlayerReady(api: VgApiService, mediaId: string) {
    if (this.playerWired) {
      return;
    }
    this.playerWired = true;
    this.mediaPlayerApi = api;
    const subs = this.mediaPlayerApi.getMediaById(mediaId).subscriptions;

    subs.loadedMetadata
      .pipe(first(), takeUntil(this.componentDestroyed$))
      .subscribe((e) => {
        this.videoWidth = e.srcElement.videoWidth;
        this.videoHeight = e.srcElement.videoHeight;
        if (this.startVideoTime && this.mediaPlayerApi) {
          this.seekTo(this.startVideoTime);
        }
        this.videoTimeInput = this.formatTime(
          this.startVideoTime ?? this.mediaPlayerApi?.currentTime ?? 0
        );
      });

    interval(500)
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(
          () =>
            this.configMenuVisible &&
            !this.videoTimeInputFocused &&
            this.mediaPlayerApi != null
        )
      )
      .subscribe(() => {
        const next = this.formatTime(this.mediaPlayerApi.currentTime);
        if (next !== this.videoTimeInput) {
          this.videoTimeInput = next;
        }
      });

    subs.pause
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => (this.videoPaused = true));
    subs.play
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => (this.videoPaused = false));

    const updateRateMs = Math.max(1, Math.floor(1000 / this.frameRate));
    interval(updateRateMs)
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(() => !this.videoPaused)
      )
      .subscribe(() => {
        this.currentTime = this.mediaPlayerApi.currentTime;
        this.currentFrame = Math.floor(this.currentTime * this.frameRate);
        this.updateShowcaseStage();
        this.updateAudioVolume();
        // Game time advances by ~1s/s; dispatching every frame (50Hz) just
        // floods every NgRx subscriber with redundant work. Throttle to ~1Hz
        // — that's still ample for shift / lineup updates.
        if (
          Math.abs(this.currentTime - this.lastGameTimeDispatchAt) >= 1
        ) {
          this.lastGameTimeDispatchAt = this.currentTime;
          this.gameTimeService.updatePeriodAndGameTime(this.currentTime);
        }
      });
  }

  private seekTo(videoTime: number) {
    if (this.mediaPlayerApi != null && videoTime != null) {
      this.mediaPlayerApi.seekTime(videoTime);
    }
  }

  private safePlay() {
    // mediaPlayerApi.play() proxies HTMLMediaElement.play() which returns a
    // Promise that rejects when the browser's autoplay policy blocks it. An
    // unhandled rejection would leave the showcase stuck with no playback and
    // no feedback for the user, so bail out cleanly.
    const result = this.mediaPlayerApi?.play() as unknown as
      | Promise<void>
      | undefined;
    result?.catch?.(() => this.stopShowcase());
  }

  private updateShowcaseStage() {
    if (!this.showcaseStarted || this.startVideoTime == null) {
      return;
    }
    const rawElapsed = this.currentTime - this.startVideoTime;
    if (rawElapsed < -0.5) {
      // Genuine backward seek (more than half a second) — re-anchor and
      // restart the showcase from the intro.
      this.startVideoTime = this.currentTime;
      this.showcaseStage = -1;
      this.labelStage = null;
      this.showcaseIntro = true;
      this.showcaseComplete = false;
      return;
    }
    if (rawElapsed < 0) {
      // Sub-frame floating-point noise around startVideoTime — ignore.
      return;
    }

    if (rawElapsed < this.INTRO_DURATION_SECONDS) {
      this.showcaseIntro = true;
      this.showcaseStage = -1;
      this.labelStage = null;
      return;
    }
    this.showcaseIntro = false;
    const elapsed = rawElapsed - this.INTRO_DURATION_SECONDS;

    const active = this.activeStages;
    let segmentStart = 0;
    for (let i = 0; i < active.length; i++) {
      const fadeInEnd = segmentStart + this.labelFadeInSeconds;
      const holdEnd = fadeInEnd + this.labelHoldSeconds;
      const fadeOutEnd = holdEnd + this.labelFadeOutSeconds;
      const segmentEnd = fadeOutEnd + active[i].durationInSeconds;

      if (elapsed < fadeInEnd) {
        // Phase A: label of stage i fading in, effects of previous stage
        this.labelStage = i;
        this.showcaseStage = i - 1;
        return;
      }
      if (elapsed < holdEnd) {
        // Phase B: label of stage i held visible, effects of stage i on
        this.labelStage = i;
        this.showcaseStage = i;
        return;
      }
      if (elapsed < segmentEnd) {
        // Phase C+D: label fading out and effects-only wait, effects of i on
        this.labelStage = null;
        this.showcaseStage = i;
        return;
      }
      segmentStart = segmentEnd;
    }

    // Past all stages: hold final state and surface the end card. Audio has
    // already faded out during the last stage's tail, so pause immediately.
    this.showcaseStage = active.length - 1;
    this.labelStage = null;
    if (!this.showcaseComplete) {
      this.showcaseComplete = true;
      if (this.mediaPlayerApi && !this.videoPaused) {
        this.mediaPlayerApi.pause();
      }
    }
  }

  private get totalStagesDuration(): number {
    const perStageOverhead =
      this.labelFadeInSeconds +
      this.labelHoldSeconds +
      this.labelFadeOutSeconds;
    return this.activeStages.reduce(
      (acc, s) => acc + perStageOverhead + s.durationInSeconds,
      0
    );
  }

  private updateAudioVolume() {
    if (
      !this.mediaPlayerApi ||
      !this.showcaseStarted ||
      this.startVideoTime == null
    ) {
      return;
    }
    let volume: number;
    if (this.showcaseIntro || this.showcaseComplete) {
      // Muted during the intro slide and the end card.
      volume = 0;
    } else {
      const timeIntoStages =
        this.currentTime - this.startVideoTime - this.INTRO_DURATION_SECONDS;
      const timeUntilEnd = this.totalStagesDuration - timeIntoStages;
      if (timeUntilEnd < this.AUDIO_FADE_SECONDS) {
        // Linear fade-out across the last AUDIO_FADE_SECONDS of the last stage.
        volume = Math.max(0, timeUntilEnd / this.AUDIO_FADE_SECONDS);
      } else if (timeIntoStages < this.AUDIO_FADE_SECONDS) {
        // Cubic ease-in across the first AUDIO_FADE_SECONDS of the first stage.
        const t = Math.max(
          0,
          Math.min(1, timeIntoStages / this.AUDIO_FADE_SECONDS)
        );
        volume = t * t * t;
      } else {
        volume = 1;
      }
    }
    this.mediaPlayerApi.volume = Math.max(0, Math.min(1, volume));
  }

  private configHls() {
    this.hlsConfig = {
      xhrSetup(xhr: XMLHttpRequest, url: string) {
        if (url.includes('49ing.ch') && url.includes('.m3u8')) {
          url = url.replace('.m3u8', '.m3u8?ngsw-bypass=true');
          xhr.open('GET', url, true);
        }
      },
      fetchSetup(context, initParams: RequestInit) {
        if (context.url.includes('49ing.ch') && context.url.includes('.m3u8')) {
          const url = context.url.replace('.m3u8', '.m3u8?ngsw-bypass=true');
          return new Request(url, initParams);
        }
        return new Request(context.url, initParams);
      },
      enableWorker: true,
      lowLatencyMode: true,
      backBufferLength: 90,
      liveSyncDuration: 4.5
    };
  }
}
